import { useState, useRef, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Form, Button, Card, Alert } from "react-bootstrap";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import { LOGIN_ROUTE } from "../../routes/RoutesPathes";
import styles from "./forms.module.css";
import { resetPasswordAPI } from "../../services/authService";
import { StatusCircle } from "../../components/general/StatusCircle";

export const PasswordReset = () => {
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  
  const password1Ref = useRef();
  const { uid, token } = useParams();
  const { t, i18n } = useTranslation();
  const arrow = i18n.dir() === "rtl" ? "←" : "→";

  useEffect(() => {
    password1Ref?.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrMsg("");
    setSuccess(false);

    if (password1 !== password2) {
      setErrMsg(t("forms.password_reset.no_match"));
      return;
    }

    try {
      setLoading(true);
      await resetPasswordAPI(uid, token, password1);
      setPassword1("");
      setPassword2("");
      setSuccess(true);
    } catch (err) {
      setErrMsg(t("titles.something_went_wrong"));
      if (err?.response?.status === 403) {
        setErrMsg(t("forms.password_reset.reset_expired"));
      } else if (err?.response?.status === 400) {
        const passwordError = err?.response?.data?.password;
        if (Array.isArray(passwordError) && passwordError[0]) {
          setErrMsg(passwordError[0]);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      className="d-flex align-items-center justify-content-center"
      style={{ marginTop: "7rem" }}
    >
      <Row className="w-100 justify-content-center">
        <Col md={6} lg={5}>
          <Card className="p-4">
            {success ? (
              <>
                <div className='d-flex flex-column justify-content-center align-items-center'>
                  <h4 className="mb-3">{t("forms.password_reset.password_is_reset")}</h4>
                  <StatusCircle status='success'/>
                </div>
                <span className="text-center mt-3">
                  <Link to={LOGIN_ROUTE} className="s-link s-purple-link">{t("buttons.login")} {arrow}</Link>
                </span>
              </>
            ) : (
              <>
                <h4 className="mb-3 text-center">
                  {t("forms.password_reset.title")}
                </h4>
                <Form onSubmit={handleSubmit}>
                  <Form.Group as={Col} md="12" className="position-relative mb-3">
                    <Form.Control
                      className={styles.formInput}
                      type={showPassword1 ? "text" : "password"}
                      required
                      placeholder={t("forms.password_reset.password_placeholder")}
                      ref={password1Ref}
                      value={password1}
                      maxLength={100}
                      onChange={(e) => setPassword1(e.target.value)}
                    />
                    <div
                      onClick={() => setShowPassword1((prev) => !prev)}
                      style={{
                        position: "absolute",
                        top: "50%",
                        [i18n.dir() === "ltr" ? "right" : "left"]: "10px",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#666",
                      }}
                    >
                      {showPassword1 ? <BsEyeSlash color="#333" /> : <BsEye color="#333" />}
                    </div>
                  </Form.Group>

                  <Form.Group as={Col} md="12" className="position-relative">
                    <Form.Control
                      className={styles.formInput}
                      type={showPassword2 ? "text" : "password"}
                      required
                      placeholder={t("forms.password_reset.confirm_password")}
                      value={password2}
                      maxLength={100}
                      onChange={(e) => setPassword2(e.target.value)}
                    />
                    <div
                      onClick={() => setShowPassword2((prev) => !prev)}
                      style={{
                        position: "absolute",
                        top: "50%",
                        [i18n.dir() === "ltr" ? "right" : "left"]: "10px",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#666",
                      }}
                    >
                      {showPassword2 ? <BsEyeSlash color="#333" /> : <BsEye color="#333" />}
                    </div>
                  </Form.Group>

                  <Button
                    className={`${styles.formBtn} btn-secondary mt-4 w-100`}
                    type="submit"
                    disabled={loading}
                  >
                    {loading
                      ? t("buttons.loading")
                      : t("buttons.reset_password")}
                  </Button>
                  {errMsg && (
                    <Alert variant="danger" className="mt-3 mb-0">
                      <p className="m-0" style={{ fontSize: "1rem" }}>
                        {errMsg}
                      </p>
                    </Alert>
                  )}
                </Form>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};
